import React, { useEffect, useMemo, useReducer, useRef, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { Drawer } from '@material-ui/core';
import CloseWindowButton from '../../../atomic/buttons/closeWindow/CloseWindowButton';
import TextButton from '../../../atomic/buttons/textButton/TextButton';
import CheckBox from '../../../atomic/formElements/checkBox/CheckBox';
import { colors } from '../../../../colorVars';
import Account from '../tradingPlanFormComponents/FieldComponents/Account';
import Brokerage from '../tradingPlanFormComponents/FieldComponents/Brokerage';
import WorkOrders from '../tradingPlanFormComponents/FieldComponents/workOrders/WorkOrders';
import ExitTacticsContainer from '../tradingPlanFormComponents/FieldComponents/exitTactics/exitTacticsContainer/ExitTacticsContainer';
import DataList from '../../../atomic/inputs/datalist/DataList';
import ExportModal from '../templateFunctions/exportModal/ExportModal';
import ImportModal from '../templateFunctions/importModal/ImportModal';
import OverwriteModal from '../templateFunctions/overwriteModal/OverwriteModal';
import DeleteModal from '../templateFunctions/deleteModal/DeleteModal';
import { addSavedTradePlanConfig } from '../../../../stores/websocketStore/utils/outgoingMessages/tradePlan/savetradeplanconfig/addSavedTradePlanConfig';
import { updateSavedTradePlanConfig } from '../../../../stores/websocketStore/utils/outgoingMessages/tradePlan/savetradeplanconfig/updateSavedTradePlanConfig';
import { removeSavedTradePlanConfig } from '../../../../stores/websocketStore/utils/outgoingMessages/tradePlan/savetradeplanconfig/removeSavedTradePlanConfig';
import { retrieveUserTemplates } from '../../../../stores/websocketStore/utils/outgoingMessages/tradePlan/importexporttradeplan/retrieveUserTemplates';
import { useWebsocketStore } from '../../../../stores/websocketStore/websocketProvider';
import useTopReducer from '../tradingPlanFormComponents/hooks/topReducer';
import useDataMemo from '../tradingPlanFormComponents/hooks/useDataMemo';
import {
  useAtrTrail,
  useAtrTrailEnabled,
  useBreakPrice,
  useBreakPriceEnabled,
  useCandleMagnitude,
  useCandleMagnitudeEnabled,
  useCandleTrail,
  useCandleTrailEnabled,
  useExitTactics,
  useGrabCents,
  useGrabCentsDetails,
  useGrabCentsEnabled,
  useGrabCentsMode,
  useGreenToRedCandles,
  useGreenToRedCandlesEnabled,
  useGreenToRedPositions,
  useGreenToRedPositionsEnabled,
  useLossLimit,
  useLossLimitEnabled,
  useMfeRetracement,
  useMfeRetracementEnabled,
  usePositionBreakPrice,
  usePositionBreakPriceEnabled,
  usePositionPriceTrail,
  usePositionPriceTrailEnabled,
  usePreserveProfit,
  usePreserveProfitEnabled,
  useRatchetBracket,
  useRatchetBracketEnabled,
  useUnderlyingPriceExit,
  useUnderlyingPriceExitEnabled,
  useUnderlyingPriceTrail,
  useUnderlyingPriceTrailEnabled,
} from '../tradingPlanFormComponents/hooks/tactics/ExitTacticHooks';
import {
  WRAP_SOURCE_MODE,
  buildWrappedTradePlanPayload,
  calculateWrapPreview,
  createWrapRequestId,
  deriveWrapBuckets,
  sanitizeTemplateForWrap,
  summarizeSelection,
  toggleWrapSelection,
  validateWrapSelection,
} from './wrapTradePlanUtils';
import { requestWrapTradePlanCandidates } from '../../../../stores/websocketStore/utils/outgoingMessages/tradePlan/wrapExistingPosition/requestWrapTradePlanCandidates';
import { addWrappedTradePlan } from '../../../../stores/websocketStore/utils/outgoingMessages/tradePlan/wrapExistingPosition/addWrappedTradePlan';
import {
  TEMPLATE_TYPES,
  addTemplateTypeToPayload,
  resolveTemplateSaveBehavior,
} from '../templateFunctions/templateTypeUtils';
import style from './WrapExistingPositionDrawer.module.css';

function signedNumber(value) {
  const numeric = Number(value ?? 0);
  if (!Number.isFinite(numeric)) return '0';
  return `${numeric > 0 ? '+' : ''}${numeric}`;
}

function priceNumber(value) {
  const numeric = Number(value ?? 0);
  if (!Number.isFinite(numeric) || numeric === 0) return '-';
  return numeric.toFixed(2);
}


function formatCalendarDate(datePart) {
  const [year, month, day] = String(datePart || '')
    .split('-')
    .map((value) => Number(value));
  if (!year || !month || !day) return datePart;
  return new Intl.DateTimeFormat([], {
    month: 'short',
    day: 'numeric',
  }).format(new Date(Date.UTC(year, month - 1, day, 12)));
}

function formatDateTime(value) {
  if (!value) return '-';
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    return formatCalendarDate(value);
  }
  if (/^\d{4}-\d{2}-\d{2}T00:00:00(?:\.000)?Z$/.test(value)) {
    return formatCalendarDate(value.slice(0, 10));
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString([], {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function orderFlowLabel(row) {
  const signedQty = Number(row?.signedRemainingQuantity ?? row?.signedFilledQuantity ?? 0);
  return signedQty < 0 ? 'Exit' : 'Entry';
}

function orderFlowClass(row) {
  const signedQty = Number(row?.signedRemainingQuantity ?? row?.signedFilledQuantity ?? 0);
  return signedQty < 0 ? style.flowExit : style.flowEntry;
}

function describeSelectedSource(row) {
  if (!row) return 'Selected source';
  if (row.sourceType === 'order') {
    const date = row.transactionDate || row.createDate;
    const flow = orderFlowLabel(row);
    return date
      ? `${flow} ${row.symbol} ${formatDateTime(date)}`
      : `${flow} ${row.symbol}`;
  }
  return 'Position allocation';
}

function buildInitialAccountSelection() {
  return {
    dataSource: 'LIVE',
    accountNumber: 'none',
    brokerage: 'none',
  };
}

function buildRowsForMode(candidates, sourceMode) {
  if (!candidates) return [];
  const rows = sourceMode === WRAP_SOURCE_MODE.POSITIONS
    ? candidates.positions ?? []
    : candidates.orders ?? [];
  if (sourceMode === WRAP_SOURCE_MODE.ORDERS) {
    return [...rows].sort((left, right) => {
      const leftTime = new Date(left.transactionDate || left.createDate || 0).getTime();
      const rightTime = new Date(right.transactionDate || right.createDate || 0).getTime();
      if (leftTime !== rightTime) return rightTime - leftTime;
      return String(left.symbol || '').localeCompare(String(right.symbol || ''));
    });
  }
  return [...rows].sort((left, right) =>
    String(left.symbol || '').localeCompare(String(right.symbol || ''))
  );
}

const WrapTradePlanConfigPanel = observer(
  ({ selectedBucket, onConfigChange, focusRef }) => {
    const { savedTradePlanTemplatesStore } = useWebsocketStore();
    const [top, dispatchTop] = useTopReducer();

    const [configTitle, setConfigTitle] = useState('');
    const [message, setMessage] = useState('');
    const [messageType, setMessageType] = useState('');
    const [dataListVal, setDataListVal] = useState('');
    const [exportOpen, setExportOpen] = useState(false);
    const [importOpen, setImportOpen] = useState(false);
    const [overwriteOpen, setOverwriteOpen] = useState(false);
    const [deleteOpen, setDeleteOpen] = useState(false);
    const [deleteResult, setDeleteResult] = useState({});

    const [lossLimitEnabled, setLossLimitEnabled] = useLossLimitEnabled();
    const [lossLimit, dispatchLossLimit] = useLossLimit();
    const [preserveProfitEnabled, setPreserveProfitEnabled] =
      usePreserveProfitEnabled();
    const [preserveProfit, dispatchPreserveProfit] = usePreserveProfit();
    const [positionPriceTrailEnabled, setPositionPriceTrailEnabled] =
      usePositionPriceTrailEnabled();
    const [positionPriceTrail, dispatchPositionPriceTrail] =
      usePositionPriceTrail();
    const [underlyingPriceTrailEnabled, setUnderlyingPriceTrailEnabled] =
      useUnderlyingPriceTrailEnabled();
    const [underlyingPriceTrail, dispatchUnderlyingPriceTrail] =
      useUnderlyingPriceTrail();
    const [candleTrailEnabled, setCandleTrailEnabled] =
      useCandleTrailEnabled();
    const [candleTrail, dispatchCandleTrail] = useCandleTrail();
    const [grabCentsEnabled, setGrabCentsEnabled] = useGrabCentsEnabled();
    const [grabCents, dispatchGrabCents] = useGrabCents();
    const [grabCentsMode, setGrabCentsMode] = useGrabCentsMode();
    const grabCentsDetails = useGrabCentsDetails(
      [grabCents, dispatchGrabCents],
      [grabCentsMode, setGrabCentsMode]
    );
    const [ratchetBracketEnabled, setRatchetBracketEnabled] =
      useRatchetBracketEnabled();
    const [ratchetBracket, dispatchRatchetBracket] = useRatchetBracket();
    const [greenToRedCandlesEnabled, setGreenToRedCandlesEnabled] =
      useGreenToRedCandlesEnabled();
    const [greenToRedCandles, dispatchGreenToRedCandles] =
      useGreenToRedCandles();
    const [greenToRedPositionsEnabled, setGreenToRedPositionsEnabled] =
      useGreenToRedPositionsEnabled();
    const [greenToRedPositions, dispatchGreenToRedPositions] =
      useGreenToRedPositions();
    const [breakPriceEnabled, setBreakPriceEnabled] = useBreakPriceEnabled();
    const [breakPrice, dispatchBreakPrice] = useBreakPrice();
    const [positionBreakPriceEnabled, setPositionBreakPriceEnabled] =
      usePositionBreakPriceEnabled();
    const [positionBreakPrice, dispatchPositionBreakPrice] =
      usePositionBreakPrice();
    const [candleMagnitudeEnabled, setCandleMagnitudeEnabled] =
      useCandleMagnitudeEnabled();
    const [candleMagnitude, dispatchCandleMagnitude] = useCandleMagnitude();
    const [atrTrailEnabled, setAtrTrailEnabled] = useAtrTrailEnabled();
    const [atrTrail, dispatchAtrTrail] = useAtrTrail();
    const [mfeRetracementEnabled, setMfeRetracementEnabled] =
      useMfeRetracementEnabled();
    const [mfeRetracement, dispatchMfeRetracement] = useMfeRetracement();
    const [underlyingPriceExitEnabled, setUnderlyingPriceExitEnabled] =
      useUnderlyingPriceExitEnabled();
    const [underlyingPriceExit, dispatchUnderlyingPriceExit] =
      useUnderlyingPriceExit();

    const defaultExitStatesRef = useRef(null);
    if (!defaultExitStatesRef.current) {
      defaultExitStatesRef.current = {
        LOSS_LIMIT: { ...lossLimit },
        PRESERVE_PROFIT: { ...preserveProfit },
        POSITION_PRICE_TRAIL: { ...positionPriceTrail },
        UNDERLYING_PRICE_TRAIL: { ...underlyingPriceTrail },
        CANDLE_TRAIL: { ...candleTrail },
        GRAB_CENTS: { ...grabCents },
        GRAB_CENTS_MODE: grabCentsMode,
        RATCHET_BRACKET: { ...ratchetBracket },
        GREEN_TO_RED_CANDLES: { ...greenToRedCandles },
        GREEN_TO_RED_POSITIONS: { ...greenToRedPositions },
        BREAK_PRICE: { ...breakPrice },
        POSITION_BREAK_PRICE: { ...positionBreakPrice },
        CANDLE_MAGNITUDE: { ...candleMagnitude },
        ATR_TRAIL: { ...atrTrail },
        MFE_RETRACEMENT: { ...mfeRetracement },
        UNDERLYING_PRICE_EXIT: { ...underlyingPriceExit },
      };
    }

    useEffect(() => {
      dispatchTop({
        status: 'ARMED',
        dataSource: 'LIVE',
        quantityBasis: 'DOLLARS',
        workOrdersOnEntry: false,
        terminateWhenFlat: true,
        setup: 'AD_HOC',
      });
    }, [dispatchTop]);

    useEffect(() => {
      if (!selectedBucket) {
        dispatchTop({
          symbol: '',
          brokerage: top.brokerage === 'none' ? 'none' : top.brokerage,
          accountNumber:
            top.accountNumber === 'none' ? 'none' : top.accountNumber,
        });
        return;
      }

      dispatchTop({
        symbol: selectedBucket.symbol,
        brokerage: selectedBucket.brokerage,
        accountNumber: selectedBucket.accountNumber,
        instrument: selectedBucket.instrument ?? 'stock',
        goLong: selectedBucket.direction === 'LONG',
        goShort: selectedBucket.direction === 'SHORT',
        dataSource: 'LIVE',
        quantityBasis: 'DOLLARS',
        workOrdersOnEntry: false,
        terminateWhenFlat: true,
        setup: 'AD_HOC',
      });
    }, [dispatchTop, selectedBucket, top.accountNumber, top.brokerage]);

    const exitTactics = useExitTactics(
      [lossLimitEnabled, setLossLimitEnabled],
      [lossLimit, dispatchLossLimit],
      [preserveProfitEnabled, setPreserveProfitEnabled],
      [preserveProfit, dispatchPreserveProfit],
      [positionPriceTrailEnabled, setPositionPriceTrailEnabled],
      [positionPriceTrail, dispatchPositionPriceTrail],
      [underlyingPriceTrailEnabled, setUnderlyingPriceTrailEnabled],
      [underlyingPriceTrail, dispatchUnderlyingPriceTrail],
      [grabCentsEnabled, setGrabCentsEnabled],
      [grabCents, dispatchGrabCents],
      grabCentsDetails,
      [ratchetBracketEnabled, setRatchetBracketEnabled],
      [ratchetBracket, dispatchRatchetBracket],
      [candleTrailEnabled, setCandleTrailEnabled],
      [candleTrail, dispatchCandleTrail],
      [greenToRedCandlesEnabled, setGreenToRedCandlesEnabled],
      [greenToRedPositionsEnabled, setGreenToRedPositionsEnabled],
      [greenToRedCandles, dispatchGreenToRedCandles],
      [greenToRedPositions, dispatchGreenToRedPositions],
      [breakPriceEnabled, setBreakPriceEnabled],
      [breakPrice, dispatchBreakPrice],
      [positionBreakPriceEnabled, setPositionBreakPriceEnabled],
      [positionBreakPrice, dispatchPositionBreakPrice],
      [candleMagnitudeEnabled, setCandleMagnitudeEnabled],
      [candleMagnitude, dispatchCandleMagnitude],
      [atrTrailEnabled, setAtrTrailEnabled],
      [atrTrail, dispatchAtrTrail],
      [mfeRetracementEnabled, setMfeRetracementEnabled],
      [mfeRetracement, dispatchMfeRetracement],
      [underlyingPriceExitEnabled, setUnderlyingPriceExitEnabled],
      [underlyingPriceExit, dispatchUnderlyingPriceExit]
    );

    const data = useDataMemo(
      top,
      {},
      false,
      {},
      {},
      exitTactics,
      {},
      {},
      { AD_HOC: {} },
      {}
    );

    useEffect(() => {
      onConfigChange({
        ...data,
        savedConfigurationTitle: configTitle,
      });
    }, [configTitle, data, onConfigChange]);

    const resetTactics = () => {
      setLossLimitEnabled(false);
      dispatchLossLimit(defaultExitStatesRef.current.LOSS_LIMIT);
      setPreserveProfitEnabled(false);
      dispatchPreserveProfit(defaultExitStatesRef.current.PRESERVE_PROFIT);
      setPositionPriceTrailEnabled(false);
      dispatchPositionPriceTrail(
        defaultExitStatesRef.current.POSITION_PRICE_TRAIL
      );
      setUnderlyingPriceTrailEnabled(false);
      dispatchUnderlyingPriceTrail(
        defaultExitStatesRef.current.UNDERLYING_PRICE_TRAIL
      );
      setCandleTrailEnabled(false);
      dispatchCandleTrail(defaultExitStatesRef.current.CANDLE_TRAIL);
      setGrabCentsEnabled(false);
      dispatchGrabCents(defaultExitStatesRef.current.GRAB_CENTS);
      setGrabCentsMode(defaultExitStatesRef.current.GRAB_CENTS_MODE);
      setRatchetBracketEnabled(false);
      dispatchRatchetBracket(defaultExitStatesRef.current.RATCHET_BRACKET);
      setGreenToRedCandlesEnabled(false);
      dispatchGreenToRedCandles(
        defaultExitStatesRef.current.GREEN_TO_RED_CANDLES
      );
      setGreenToRedPositionsEnabled(false);
      dispatchGreenToRedPositions(
        defaultExitStatesRef.current.GREEN_TO_RED_POSITIONS
      );
      setBreakPriceEnabled(false);
      dispatchBreakPrice(defaultExitStatesRef.current.BREAK_PRICE);
      setPositionBreakPriceEnabled(false);
      dispatchPositionBreakPrice(
        defaultExitStatesRef.current.POSITION_BREAK_PRICE
      );
      setCandleMagnitudeEnabled(false);
      dispatchCandleMagnitude(defaultExitStatesRef.current.CANDLE_MAGNITUDE);
      setAtrTrailEnabled(false);
      dispatchAtrTrail(defaultExitStatesRef.current.ATR_TRAIL);
      setMfeRetracementEnabled(false);
      dispatchMfeRetracement(defaultExitStatesRef.current.MFE_RETRACEMENT);
      setUnderlyingPriceExitEnabled(false);
      dispatchUnderlyingPriceExit(
        defaultExitStatesRef.current.UNDERLYING_PRICE_EXIT
      );
    };

    const applyTemplate = (template) => {
      const sanitized = sanitizeTemplateForWrap(template);
      dispatchTop({
        flattenAtMarketClose: Boolean(sanitized.flattenAtMarketClose),
        workOrders: Boolean(sanitized.workOrders),
        workOrdersOnExit:
          sanitized.workOrdersOnExit !== undefined
            ? sanitized.workOrdersOnExit
            : true,
        useDifferentWorkOrderFieldsOnExit: Boolean(
          sanitized.useDifferentWorkOrderFieldsOnExit
        ),
        useDifferentWorkOrderFieldsOnTarget: Boolean(
          sanitized.useDifferentWorkOrderFieldsOnTarget
        ),
        getAwayOffsetPct:
          sanitized.getAwayOffsetPct !== undefined
            ? sanitized.getAwayOffsetPct
            : 0.1,
        pctBetweenBidAsk:
          sanitized.pctBetweenBidAsk !== undefined
            ? sanitized.pctBetweenBidAsk
            : 0.5,
        pctIncreasePerOrderAttempt:
          sanitized.pctIncreasePerOrderAttempt !== undefined
            ? sanitized.pctIncreasePerOrderAttempt
            : 0.1,
        getAwayOffsetPctOnExit:
          sanitized.getAwayOffsetPctOnExit !== undefined
            ? sanitized.getAwayOffsetPctOnExit
            : 0.1,
        pctBetweenBidAskOnExit:
          sanitized.pctBetweenBidAskOnExit !== undefined
            ? sanitized.pctBetweenBidAskOnExit
            : 0.5,
        pctIncreasePerOrderAttemptOnExit:
          sanitized.pctIncreasePerOrderAttemptOnExit !== undefined
            ? sanitized.pctIncreasePerOrderAttemptOnExit
            : 0.1,
        getAwayOffsetPctOnTarget:
          sanitized.getAwayOffsetPctOnTarget !== undefined
            ? sanitized.getAwayOffsetPctOnTarget
            : 0.1,
        pctBetweenBidAskOnTarget:
          sanitized.pctBetweenBidAskOnTarget !== undefined
            ? sanitized.pctBetweenBidAskOnTarget
            : 0.5,
        pctIncreasePerOrderAttemptOnTarget:
          sanitized.pctIncreasePerOrderAttemptOnTarget !== undefined
            ? sanitized.pctIncreasePerOrderAttemptOnTarget
            : 0.1,
      });

      resetTactics();
      const exitConfig = sanitized.exitTactics ?? {};
      if (exitConfig.LOSS_LIMIT) {
        setLossLimitEnabled(true);
        dispatchLossLimit(exitConfig.LOSS_LIMIT);
      }
      if (exitConfig.PRESERVE_PROFIT) {
        setPreserveProfitEnabled(true);
        dispatchPreserveProfit(exitConfig.PRESERVE_PROFIT);
      }
      if (exitConfig.POSITION_PRICE_TRAIL) {
        setPositionPriceTrailEnabled(true);
        dispatchPositionPriceTrail(exitConfig.POSITION_PRICE_TRAIL);
      }
      if (exitConfig.UNDERLYING_PRICE_TRAIL) {
        setUnderlyingPriceTrailEnabled(true);
        dispatchUnderlyingPriceTrail(exitConfig.UNDERLYING_PRICE_TRAIL);
      }
      if (exitConfig.CANDLE_TRAIL) {
        setCandleTrailEnabled(true);
        dispatchCandleTrail(exitConfig.CANDLE_TRAIL);
      }
      if (exitConfig.GRAB_CENTS) {
        setGrabCentsEnabled(true);
        dispatchGrabCents(exitConfig.GRAB_CENTS);
        setGrabCentsMode(
          exitConfig.GRAB_CENTS.quantityPerLevel ? 'quantity' : 'percentage'
        );
      }
      if (exitConfig.RATCHET_BRACKET) {
        setRatchetBracketEnabled(true);
        dispatchRatchetBracket(exitConfig.RATCHET_BRACKET);
      }
      if (exitConfig.GREEN_TO_RED_CANDLES) {
        setGreenToRedCandlesEnabled(true);
        dispatchGreenToRedCandles(exitConfig.GREEN_TO_RED_CANDLES);
      }
      if (exitConfig.GREEN_TO_RED_POSITIONS) {
        setGreenToRedPositionsEnabled(true);
        dispatchGreenToRedPositions(exitConfig.GREEN_TO_RED_POSITIONS);
      }
      if (exitConfig.BREAK_PRICE) {
        setBreakPriceEnabled(true);
        dispatchBreakPrice(exitConfig.BREAK_PRICE);
      }
      if (exitConfig.POSITION_BREAK_PRICE) {
        setPositionBreakPriceEnabled(true);
        dispatchPositionBreakPrice(exitConfig.POSITION_BREAK_PRICE);
      }
      if (exitConfig.CANDLE_MAGNITUDE) {
        setCandleMagnitudeEnabled(true);
        dispatchCandleMagnitude(exitConfig.CANDLE_MAGNITUDE);
      }
      if (exitConfig.ATR_TRAIL) {
        setAtrTrailEnabled(true);
        dispatchAtrTrail(exitConfig.ATR_TRAIL);
      }
      if (exitConfig.MFE_RETRACEMENT) {
        setMfeRetracementEnabled(true);
        dispatchMfeRetracement(exitConfig.MFE_RETRACEMENT);
      }
      if (exitConfig.UNDERLYING_PRICE_EXIT) {
        setUnderlyingPriceExitEnabled(true);
        dispatchUnderlyingPriceExit(exitConfig.UNDERLYING_PRICE_EXIT);
      }

      const title = sanitized.savedConfigurationTitle ?? dataListVal;
      setConfigTitle(title);
      setDataListVal(title);
      setMessage('Template loaded.');
      setMessageType('success');
    };

    const templates = savedTradePlanTemplatesStore.savedTemplates;
    const templatesList = savedTradePlanTemplatesStore.templatesList;
    const currentTemplateData = sanitizeTemplateForWrap({
      ...data,
      savedConfigurationTitle: configTitle,
    });

    const findTemplate = () => templates[dataListVal];
    const saveTemplate = () => {
      if (dataListVal === '') {
        setMessage('Please give your template a name.');
        setMessageType('error');
        return;
      }

      const saveBehavior = resolveTemplateSaveBehavior({
        requestedName: dataListVal,
        templateType: TEMPLATE_TYPES.WRAP,
        existingTemplates: templates,
      });

      if (saveBehavior.shouldPromptOverwrite) {
        setOverwriteOpen(true);
        return;
      }

      addSavedTradePlanConfig(
        saveBehavior.saveName,
        addTemplateTypeToPayload(currentTemplateData, TEMPLATE_TYPES.WRAP),
        TEMPLATE_TYPES.WRAP
      );
      setDataListVal(saveBehavior.saveName);
      setConfigTitle(saveBehavior.saveName);
      setMessage(
        saveBehavior.usedSaveAsNew
          ? `Template saved as ${saveBehavior.saveName}.`
          : 'Template saved.'
      );
      setMessageType('success');
    };

    const overwriteTemplate = () => {
      const existingTemplate = findTemplate();
      if (!existingTemplate) return;
      updateSavedTradePlanConfig({
        tradePlanObject: addTemplateTypeToPayload(
          currentTemplateData,
          TEMPLATE_TYPES.WRAP
        ),
        key: existingTemplate.key,
        configName: dataListVal,
        templateType: TEMPLATE_TYPES.WRAP,
      });
      setConfigTitle(dataListVal);
      setOverwriteOpen(false);
      setMessage('Template saved.');
      setMessageType('success');
    };

    const loadTemplate = () => {
      const template = findTemplate();
      if (!template) {
        setMessage('Template does not exist.');
        setMessageType('error');
        return;
      }
      applyTemplate(template.tradePlanJson);
    };

    const deleteTemplate = () => {
      const template = findTemplate();
      if (!template) return;
      removeSavedTradePlanConfig({
        key: template.key,
        configName: template.configurationName,
      });
      setDataListVal('');
      setConfigTitle('');
      setMessage('Template deleted.');
      setMessageType('success');
    };

    return (
      <div className={style.section}>
        <h3 className={style.sectionTitle}>Exit And Template Configuration</h3>
        <p className={style.muted}>
          Templates in this flow only save wrap-compatible settings. Entry, setup,
          and restart fields are stripped on save and ignored on load.
        </p>
        <p className={style.muted}>
          Wrap mode starts from the live position as it exists now. Configure exits against the current open quantity and basis.
        </p>
        <div style={{ marginTop: 12 }}>
          <DataList
            dataList={templatesList}
            dataMapFunc={(obj) => (
              <option key={obj.key} value={obj.configurationName} />
            )}
            inputVal={dataListVal}
            inputHandler={(value) => {
              setDataListVal(value);
              setMessage('');
            }}
            id="wrap-template"
            ref={focusRef}
            onBlur={() => setConfigTitle(dataListVal)}
          >
            Wrap Template Name
          </DataList>
        </div>
        <p
          className={
            messageType === 'error' ? style.messageError : style.messageSuccess
          }
          style={{ marginTop: 8 }}
        >
          {message}
        </p>
        <div className={style.templateButtons}>
          <TextButton
            fontSize="medium"
            color="white"
            backgroundColor={colors.blue}
            clickHandler={saveTemplate}
          >
            Save
          </TextButton>
          <TextButton
            fontSize="medium"
            color="white"
            backgroundColor={colors.blue}
            clickHandler={loadTemplate}
          >
            Load
          </TextButton>
          <TextButton
            fontSize="medium"
            color="white"
            backgroundColor={colors.blue}
            clickHandler={() => {
              const template = findTemplate();
              if (!template) {
                setMessage('Template does not exist.');
                setMessageType('error');
                return;
              }
              setDeleteResult(template);
              setDeleteOpen(true);
            }}
          >
            Delete
          </TextButton>
          <TextButton
            fontSize="medium"
            color="white"
            backgroundColor={colors.green}
            clickHandler={() => {
              setConfigTitle('');
              setExportOpen(true);
              retrieveUserTemplates();
            }}
          >
            Share
          </TextButton>
          <TextButton
            fontSize="medium"
            color="white"
            backgroundColor={colors.green}
            clickHandler={() => setImportOpen(true)}
          >
            Import
          </TextButton>
        </div>

        <OverwriteModal
          configName={dataListVal}
          open={overwriteOpen}
          handleClose={() => setOverwriteOpen(false)}
          updateConfig={overwriteTemplate}
        />
        <DeleteModal
          result={deleteResult}
          deleteConfig={deleteTemplate}
          open={deleteOpen}
          handleClose={() => setDeleteOpen(false)}
        />
        <ExportModal
          open={exportOpen}
          handleClose={() => setExportOpen(false)}
          data={currentTemplateData}
        />
        <ImportModal
          open={importOpen}
          handleClose={() => setImportOpen(false)}
          handleLoad={applyTemplate}
        />

        <div style={{ marginTop: 14 }} className={style.inlineFields}>
          <div>
            <CheckBox
              margin="0 3px 0 0"
              checked={top.flattenAtMarketClose}
              onChange={(checked) =>
                dispatchTop({
                  flattenAtMarketClose: checked,
                })
              }
            >
              Flatten At Market Close
            </CheckBox>
          </div>
          <div className={style.lockedBadge}>
            Entry, setup, and restart controls are intentionally hidden.
          </div>
        </div>

        <div style={{ marginTop: 14 }}>
          <WorkOrders top={top} dispatchTop={dispatchTop} />
        </div>

        <div style={{ marginTop: 14 }}>
          <ExitTacticsContainer
            top={top}
            lossLimit={[
              lossLimit,
              dispatchLossLimit,
              lossLimitEnabled,
              setLossLimitEnabled,
            ]}
            preserveProfit={[
              preserveProfit,
              dispatchPreserveProfit,
              preserveProfitEnabled,
              setPreserveProfitEnabled,
            ]}
            positionPriceTrail={[
              positionPriceTrail,
              dispatchPositionPriceTrail,
              positionPriceTrailEnabled,
              setPositionPriceTrailEnabled,
            ]}
            underlyingPriceTrail={[
              underlyingPriceTrail,
              dispatchUnderlyingPriceTrail,
              underlyingPriceTrailEnabled,
              setUnderlyingPriceTrailEnabled,
            ]}
            candleTrail={[
              candleTrail,
              dispatchCandleTrail,
              candleTrailEnabled,
              setCandleTrailEnabled,
            ]}
            grabCents={[
              grabCents,
              dispatchGrabCents,
              grabCentsEnabled,
              setGrabCentsEnabled,
              grabCentsMode,
              setGrabCentsMode,
            ]}
            ratchetBracket={[
              ratchetBracket,
              dispatchRatchetBracket,
              ratchetBracketEnabled,
              setRatchetBracketEnabled,
            ]}
            greenToRedCandles={[
              greenToRedCandles,
              dispatchGreenToRedCandles,
              greenToRedCandlesEnabled,
              setGreenToRedCandlesEnabled,
            ]}
            greenToRedPositions={[
              greenToRedPositions,
              dispatchGreenToRedPositions,
              greenToRedPositionsEnabled,
              setGreenToRedPositionsEnabled,
            ]}
            breakPrice={[
              breakPrice,
              dispatchBreakPrice,
              breakPriceEnabled,
              setBreakPriceEnabled,
            ]}
            candleMagnitude={[
              candleMagnitude,
              dispatchCandleMagnitude,
              candleMagnitudeEnabled,
              setCandleMagnitudeEnabled,
            ]}
            atrTrail={[
              atrTrail,
              dispatchAtrTrail,
              atrTrailEnabled,
              setAtrTrailEnabled,
            ]}
            mfeRetracement={[
              mfeRetracement,
              dispatchMfeRetracement,
              mfeRetracementEnabled,
              setMfeRetracementEnabled,
            ]}
            underlyingPriceExit={[
              underlyingPriceExit,
              dispatchUnderlyingPriceExit,
              underlyingPriceExitEnabled,
              setUnderlyingPriceExitEnabled,
            ]}
            positionBreakPrice={[
              positionBreakPrice,
              dispatchPositionBreakPrice,
              positionBreakPriceEnabled,
              setPositionBreakPriceEnabled,
            ]}
          />
        </div>
      </div>
    );
  }
);

export const WrapExistingPositionDrawer = observer(
  ({ open, onClose, focusRef }) => {
    const { wrapTradePlanStore } = useWebsocketStore();
    const [accountSelection, dispatchAccountSelection] = useReducer(
      (state, action) => ({ ...state, ...action }),
      buildInitialAccountSelection()
    );
    const sourceMode = WRAP_SOURCE_MODE.POSITIONS;
    const [selectedRows, setSelectedRows] = useState({});
    const [quantitiesById, setQuantitiesById] = useState({});
    const [selectionMessage, setSelectionMessage] = useState('');
    const [submitMessage, setSubmitMessage] = useState('');
    const [tradePlanConfig, setTradePlanConfig] = useState({});

    const candidates = wrapTradePlanStore.activeCandidates;
    const requestStatus = wrapTradePlanStore.activeRequestStatus;
    const rows = useMemo(
      () => buildRowsForMode(candidates, sourceMode),
      [candidates, sourceMode]
    );
    const displayedRows = rows;
    const bucketsByKey = useMemo(() => deriveWrapBuckets(candidates), [candidates]);
    const selectionSummary = useMemo(
      () => summarizeSelection({ selectedRows, quantitiesById }),
      [selectedRows, quantitiesById]
    );
    const selectionPreview = useMemo(
      () =>
        calculateWrapPreview({
          selectedRows,
          quantitiesById,
          bucketsByKey,
        }),
      [bucketsByKey, quantitiesById, selectedRows]
    );
    const selectedBucket =
      bucketsByKey[selectionSummary.selectedBucketKey] ??
      selectionSummary.rows[0] ??
      null;
    const sortedSelectedRows = useMemo(
      () => buildRowsForMode({ positions: selectionSummary.rows, orders: selectionSummary.rows }, sourceMode),
      [selectionSummary.rows, sourceMode]
    );
    const summaryFields = useMemo(() => {
      const fields = [
        ['Selected Rows', selectionSummary.rows.length],
        ['Symbol', selectedBucket?.symbol],
        ['Direction', selectedBucket?.direction],
        ['Brokerage', selectedBucket?.brokerage],
        ['Account', selectedBucket?.accountNumber],
        ['Aggregate Signed Qty', signedNumber(selectionSummary.signedQuantity)],
        ['Qty Remaining', signedNumber(selectedBucket?.signedRemainingQuantity ?? selectedBucket?.bucketSignedRemainingQuantity)],
      ];

      if (selectionPreview.entryPrice != null) {
        fields.push(['Entry Price', priceNumber(selectionPreview.entryPrice)]);
      }
      if (selectionPreview.realizedPnl != null) {
        fields.push(['Realized PnL (FIFO)', signedNumber(selectionPreview.realizedPnl.toFixed(2))]);
      }
      if (selectionPreview.valuationPrice != null) {
        fields.push(['Current Mark', priceNumber(selectionPreview.valuationPrice)]);
      }
      if (selectionPreview.unrealizedPnl != null) {
        fields.push(['Estimated uPnL', signedNumber(selectionPreview.unrealizedPnl.toFixed(2))]);
      }
      if (selectionPreview.percentPnl != null) {
        fields.push(['Estimated %PnL', `${selectionPreview.percentPnl.toFixed(2)}%`]);
      }
      if (selectionPreview.bid != null || selectionPreview.ask != null) {
        fields.push([
          'Bid / Ask',
          `${priceNumber(selectionPreview.bid)} / ${priceNumber(selectionPreview.ask)}`,
        ]);
      }

      return fields.filter(([, value]) => value !== null && value !== undefined && value !== '');
    }, [selectionPreview, selectionSummary.rows.length, selectionSummary.signedQuantity, selectedBucket]);

    useEffect(() => {
      if (!open) {
        wrapTradePlanStore.resetSubmitResult();
        return;
      }

      const validAccount =
        accountSelection.accountNumber &&
        accountSelection.accountNumber !== 'none' &&
        accountSelection.accountNumber !== 'BACKTEST' &&
        accountSelection.brokerage &&
        accountSelection.brokerage !== 'none' &&
        accountSelection.brokerage !== 'BACKTEST';

      if (!validAccount) {
        return;
      }

      requestWrapTradePlanCandidates({
        requestId: createWrapRequestId(),
        brokerage: accountSelection.brokerage,
        accountNumber: accountSelection.accountNumber,
      });
    }, [
      accountSelection.accountNumber,
      accountSelection.brokerage,
      open,
      wrapTradePlanStore,
    ]);

    useEffect(() => {
      setSelectedRows({});
      setQuantitiesById({});
      setSelectionMessage('');
      setSubmitMessage('');
    }, [accountSelection.accountNumber, accountSelection.brokerage]);


    useEffect(() => {
      if (!wrapTradePlanStore.submitResult) {
        return;
      }

      if (wrapTradePlanStore.submitResult.success) {
        wrapTradePlanStore.resetSubmitResult();
        setSelectedRows({});
        setQuantitiesById({});
        setSelectionMessage('');
        setSubmitMessage('');
        onClose();
        return;
      }

      setSubmitMessage(
        wrapTradePlanStore.submitResult.errorMessage ||
          'Unable to wrap this position.'
      );
    }, [onClose, wrapTradePlanStore, wrapTradePlanStore.submitResult]);

    const handleToggleRow = (row) => {
      if (
        selectionSummary.selectedBucketKey &&
        row.bucketKey !== selectionSummary.selectedBucketKey
      ) {
        setSelectionMessage(
          'Only one symbol/account/direction bucket can be wrapped at a time.'
        );
        return;
      }

      const bucket =
        bucketsByKey[row.bucketKey] ?? {
          signedRemainingQuantity: row.bucketSignedRemainingQuantity,
        };
      const next = toggleWrapSelection({
        row,
        selectedRows,
        quantitiesById,
        sourceMode,
        bucket,
      });
      setSelectedRows(next.selectedRows);
      setQuantitiesById(next.quantitiesById);
      setSelectionMessage('');
      setSubmitMessage('');
    };

    const handleSubmit = () => {
      const validation = validateWrapSelection({
        sourceMode,
        selectedRows,
        quantitiesById,
        bucketsByKey,
      });

      if (!validation.valid) {
        setSubmitMessage(validation.message);
        return;
      }

      const payload = buildWrappedTradePlanPayload({
        tradePlan: tradePlanConfig,
        sourceMode,
        selectedRows,
        quantitiesById,
      });

      addWrappedTradePlan({
        requestId: createWrapRequestId(),
        tradePlan: payload.tradePlan,
        wrapSource: payload.wrapSource,
      });
      setSubmitMessage('');
    };

    return (
      <Drawer
        anchor="right"
        open={open}
        onClose={onClose}
        classes={{ paper: { width: '800px' } }}
        PaperProps={{ style: { width: 800 } }}
      >
        <div className={style.drawerBody}>
          <div className={style.topActions}>
            <div>
              <h2 style={{ margin: '0 0 6px' }}>Wrap Existing Position</h2>
              <p className={style.muted}>
                Create a trade plan around an already-open live brokerage position.
                This flow takes over the current position state and focuses on wrap allocation plus exit behavior.
                Historical order activity is not part of wrap setup.
              </p>
            </div>
            <CloseWindowButton
              width={20}
              stroke={3}
              color="black"
              handleClick={onClose}
              margin="5px"
            />
          </div>

          <div className={style.section}>
            <h3 className={style.sectionTitle}>Brokerage And Account</h3>
            <div className={style.inlineFields}>
              <Brokerage
                top={accountSelection}
                dispatchTop={dispatchAccountSelection}
              />
              <Account
                top={accountSelection}
                dispatchTop={dispatchAccountSelection}
              />
            </div>
            <p className={style.muted} style={{ marginTop: 8 }}>
              Candidate discovery runs automatically when you select a live account.
            </p>
          </div>

          <div className={style.section}>
            <h3 className={style.sectionTitle}>Candidates</h3>
            {requestStatus.loading ? (
              <p className={style.muted}>Loading wrap candidates...</p>
            ) : null}
            {!requestStatus.loading && requestStatus.error ? (
              <p className={style.messageError}>{requestStatus.error}</p>
            ) : null}
            {!requestStatus.loading &&
            !requestStatus.error &&
            accountSelection.accountNumber === 'none' ? (
              <p className={style.muted}>Select a live account to load candidates.</p>
            ) : null}
            {!requestStatus.loading &&
            !requestStatus.error &&
            accountSelection.accountNumber !== 'none' &&
            rows.length === 0 ? (
              <p className={style.muted}>
                There are no uncovered positions available to wrap for this account.
              </p>
            ) : null}
            {selectionMessage ? (
              <p className={style.messageError} style={{ marginBottom: 8 }}>
                {selectionMessage}
              </p>
            ) : null}
            {displayedRows.map((row) => {
              const checked = Boolean(selectedRows[row.candidateId]);
              const lockedOut =
                selectionSummary.selectedBucketKey &&
                !checked &&
                row.bucketKey !== selectionSummary.selectedBucketKey;
              const isOrderRow = row.sourceType === 'order';
              const valuationSource = bucketsByKey[row.bucketKey] ?? row;
              const orderTimestamp = row.transactionDate || row.createDate;
              return (
                <div key={row.candidateId} className={style.rowCard}>
                  <div className={style.rowHeader}>
                    <CheckBox
                      checked={checked}
                      onChange={() => handleToggleRow(row)}
                      disabled={lockedOut}
                    >
                      {isOrderRow ? (
                        <span className={style.orderLabelLine}>
                          <span className={`${style.flowBadge} ${orderFlowClass(row)}`}>
                            {orderFlowLabel(row)}
                          </span>
                          <span className={style.orderTimestamp}>
                            {formatDateTime(orderTimestamp)}
                          </span>
                          <span className={style.orderSymbol}>{row.symbol}</span>
                          <span className={style.orderQty}>{signedNumber(row.signedRemainingQuantity)}</span>
                          <span className={style.orderPrice}>@ {priceNumber(row.entryPrice ?? row.avgFillPrice)}</span>
                        </span>
                      ) : (
                        `${row.symbol} ${row.direction} ${row.sourceType}`
                      )}
                    </CheckBox>
                    <input
                      className={style.quantityInput}
                      type="number"
                      step="any"
                      value={quantitiesById[row.candidateId] ?? ''}
                      disabled={!checked}
                      onChange={(event) => {
                        const nextValue =
                          event.target.value === ''
                            ? ''
                            : Number(event.target.value);
                        setQuantitiesById((prev) => ({
                          ...prev,
                          [row.candidateId]: nextValue,
                        }));
                      }}
                    />
                  </div>
                  {isOrderRow ? (
                    <div className={style.rowMetaCompact}>
                      <span>Covered {signedNumber(row.signedCoveredQuantity)}</span>
                      <span>Wrappable {signedNumber(row.signedRemainingQuantity)}</span>
                      <span>Qty Remaining {signedNumber(row.bucketSignedRemainingQuantity ?? bucketsByKey[row.bucketKey]?.signedRemainingQuantity)}</span>
                      {valuationSource.valuationPrice ? (
                        <span>Mark {priceNumber(valuationSource.valuationPrice)}</span>
                      ) : null}
                      <span>{row.accountNumber}</span>
                      <span>{row.brokerage}</span>
                    </div>
                  ) : (
                    <div className={style.rowMeta}>
                      <div>Brokerage Qty: {signedNumber(row.signedBrokerageQuantity ?? row.signedFilledQuantity)}</div>
                      <div>Already Covered: {signedNumber(row.signedCoveredQuantity)}</div>
                      <div>Available To Wrap: {signedNumber(row.signedRemainingQuantity)}</div>
                      <div>Entry Price: {priceNumber(row.entryPrice ?? row.avgFillPrice)}</div>
                      {valuationSource.valuationPrice ? (
                        <div>Current Mark: {priceNumber(valuationSource.valuationPrice)}</div>
                      ) : null}
                      <div>
                        Qty Remaining:{' '}
                        {signedNumber(
                          row.bucketSignedRemainingQuantity ??
                            bucketsByKey[row.bucketKey]?.signedRemainingQuantity
                        )}
                      </div>
                      <div>Account: {row.accountNumber}</div>
                      <div>Brokerage: {row.brokerage}</div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          <div className={style.section}>
            <h3 className={style.sectionTitle}>Selected Source Summary</h3>
            <div className={style.summaryGrid}>
              {summaryFields.map(([label, value]) => (
                <div key={label}>{label}: {value}</div>
              ))}
            </div>
            <p className={style.muted} style={{ marginTop: 8 }}>
              Preview recalculates as wrapped quantity changes and reflects the live position being taken over.
            </p>
            <div className={style.summaryRows}>
              {sortedSelectedRows.map((row) => (
                <div key={row.candidateId}>
                  {describeSelectedSource(row)}: {signedNumber(
                    quantitiesById[row.candidateId]
                  )}
                </div>
              ))}
            </div>
          </div>

          <WrapTradePlanConfigPanel
            selectedBucket={selectedBucket}
            onConfigChange={setTradePlanConfig}
            focusRef={focusRef}
          />

          <div className={style.section}>
            <TextButton
              width="100%"
              fontSize="large"
              backgroundColor={colors.green}
              color="white"
              clickHandler={handleSubmit}
            >
              Wrap Existing Position
            </TextButton>
            {submitMessage ? (
              <p className={style.messageError} style={{ marginTop: 10 }}>
                {submitMessage}
              </p>
            ) : null}
          </div>
        </div>
      </Drawer>
    );
  }
);

export default WrapExistingPositionDrawer;
